/**
 * One colour language for status pills. Every status maps to a semantic tone,
 * and every tone to complete class strings (Tailwind cannot generate classes
 * built from template literals). StatusBadge and Badge both resolve through here,
 * so the same status never shows two colours.
 */
export type Tone = 'neutral' | 'neutral-strong' | 'brand' | 'success' | 'warning' | 'error' | 'info' | 'inverse';

export const pillClasses: Record<Tone, string> = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-white/[0.08] dark:text-gray-300',
    'neutral-strong': 'bg-gray-200 text-gray-800 dark:bg-white/[0.14] dark:text-gray-200',
    brand: 'bg-brand-100 text-brand-800 dark:bg-brand-500/15 dark:text-brand-300',
    success: 'bg-success-100 text-success-700 dark:bg-success-500/15 dark:text-success-400',
    warning: 'bg-warning-100 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400',
    error: 'bg-error-100 text-error-700 dark:bg-error-500/15 dark:text-error-400',
    info: 'bg-blue-light-100 text-blue-light-700 dark:bg-blue-light-500/15 dark:text-blue-light-400',
    inverse: 'bg-gray-800 text-gray-100 dark:bg-gray-200 dark:text-gray-800',
};

export const statusTone: Record<string, Tone> = {
    draft: 'brand',
    pending: 'neutral-strong',
    viewed: 'info',
    reminded: 'info',
    info: 'info',
    approved: 'success',
    paid: 'success',
    success: 'success',
    active: 'success',
    revoked: 'warning',
    withdrawn: 'warning',
    collection: 'warning',
    paused: 'warning',
    warning: 'warning',
    expired: 'error',
    declined: 'error',
    error: 'error',
    finished: 'neutral',
    default: 'neutral',
};

/**
 * Tone colours for charts, which need literal values. They mirror the index.css
 * tokens so a chart slice matches the pill for the same status. Open work is
 * neutral gray; expired uses a lighter error shade so it stays distinct from
 * declined within one chart.
 */
export const toneHex = {
    neutral: '#98a2b3', // gray-400
    success: '#12b76a', // success-500
    warning: '#f79009', // warning-500
    error: '#f04438', // error-500
    errorSoft: '#fda29b', // error-300
} as const;

/**
 * Timeline marker classes per tone, so the dot for a status matches its pill.
 * Complete strings, because Tailwind cannot generate `ring-${tone}-300`.
 */
export const timelineRingClasses: Record<Tone, string> = {
    neutral: 'ring-gray-300 dark:ring-gray-600',
    'neutral-strong': 'ring-gray-300 dark:ring-gray-600',
    brand: 'ring-brand-300 dark:ring-brand-600',
    success: 'ring-success-300 dark:ring-success-600',
    warning: 'ring-warning-300 dark:ring-warning-600',
    error: 'ring-error-300 dark:ring-error-600',
    info: 'ring-blue-light-300 dark:ring-blue-light-600',
    inverse: 'ring-gray-300 dark:ring-gray-600',
};

export const timelineIconClasses: Record<Tone, string> = {
    neutral: 'text-gray-500',
    'neutral-strong': 'text-gray-500',
    brand: 'text-brand-600',
    success: 'text-success-500',
    warning: 'text-warning-500',
    error: 'text-error-500',
    info: 'text-blue-light-500',
    inverse: 'text-gray-500',
};

export const timelineDotClasses: Record<Tone, string> = {
    neutral: 'bg-gray-500',
    'neutral-strong': 'bg-gray-500',
    brand: 'bg-brand-500',
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    error: 'bg-error-500',
    info: 'bg-blue-light-500',
    inverse: 'bg-gray-500',
};
